import { format, parseISO } from 'date-fns';
import { detectEcommercePlatform, readStoredAmazonAccountType } from './connectionPlatform';
import { isInventorySnapshotConnectionId } from './inventoryCsvConnectionChannels';

// Connection records as stored in organisations.connections (an object keyed
// by connection id, or an array in older records).

export interface CsvImportRecord {
  id: string;
  fileName?: string;
  date?: string;
  snapshot_connection_id?: string;
  csv_import_job_id?: string;
  snapshot_date?: string;
  inventory_source?: string;
}

export interface ConnectionRecord {
  id?: string;
  connectorId?: string;
  connectionId?: string;
  name?: string;
  service?: string;
  type?: string;
  status?: unknown;
  import_type?: string;
  ecommerce_subtype?: string;
  amazon_account_type?: string;
  created_at?: string;
  csvImports?: CsvImportRecord[];
  metadata?: {
    access_token?: string;
    has_access_token?: boolean;
    error?: { error_message?: string };
    accounts?: Array<{ name?: string; mask?: string; subtype?: string }>;
    last_sync_completed_at?: string;
    [key: string]: unknown;
  };
}

export type ConnectionsMap = Record<string, ConnectionRecord> | ConnectionRecord[];

export interface ConnectionsOwner {
  id: string;
  connections?: ConnectionsMap | null;
  is_test_clone?: boolean | null;
  source_organisation_id?: string | null;
}

/** Parse YYYY-MM-DD from connector ids like `…_inventory_amazon_20260511`. */
export function parseSnapshotDateFromInventoryImportId(importId: string): string | undefined {
  const m = String(importId).match(/_inventory_(?:amazon|shopify)_(\d{8})(?:_|$)/i);
  if (!m) return undefined;
  const d = m[1];
  return `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}`;
}

/**
 * Broad CSV test the Overview Data Connections card used to decide how a
 * connection is shown (CSV rows, green status). Live-status polling keeps the
 * narrower isCsvConnectionRecord below, exactly as before.
 */
export function isCsvLikeRecord(value: ConnectionRecord & { csvMetadata?: unknown }): boolean {
  if (!value) return false;
  const service = (value.service || '').toLowerCase();
  const type = (value.type || '').toLowerCase();
  return (
    service === 'csv' ||
    type === 'csv' ||
    type === 'csv_import' ||
    !!value.connectorId?.startsWith('csv-import-') ||
    !!value.connectionId?.startsWith('csv-import-') ||
    !!value.id?.startsWith('csv-import-') ||
    !!value.csvMetadata ||
    (Array.isArray(value.csvImports) && value.csvImports.length > 0)
  );
}

export function isCsvConnectionRecord(value: ConnectionRecord): boolean {
  return value.service === 'csv' || value.type === 'csv' || value.type === 'CSV' ||
    (!!value.connectorId && value.connectorId.startsWith('csv-import-'));
}

export function isPlaidConnectionRecord(value: ConnectionRecord): boolean {
  return value.type === 'plaid' || value.service === 'plaid';
}

/** Connection entries keyed the way they are stored (object keys, or array indexes as strings). */
export function connectionEntries(connections: ConnectionsMap | null | undefined): Array<[string, ConnectionRecord]> {
  if (!connections || typeof connections !== 'object') return [];
  return Object.entries(connections) as Array<[string, ConnectionRecord]>;
}

export function connectorIdOf(key: string, value: ConnectionRecord): string {
  return value.connectorId || value.id || key;
}

// Sorting a merchant's connections into the Settings › Pricing Data In › Data
// Feeds lists (FIG-321 / REQ-042, REQ-054). A source that supplies both
// inventory and sales appears in both lists.

export interface FeedConnection {
  key: string;
  value: ConnectionRecord;
  name: string;
}

export interface CsvFile {
  connectionKey: string;
  /** Set for a file inside a grouped CSV connection; absent when the connection is the file. */
  importId?: string;
  fileName: string;
  uploadedAt?: string;
  /** Inventory files only: the date the stock count is for. */
  bindingDate?: string;
  kind: 'inventory' | 'sales' | 'other';
}

export interface MerchantFeeds {
  inventory: FeedConnection[];
  sales: FeedConnection[];
  /** Merchant's own bank accounts (Plaid); shown under Licensed Seller Monitoring. */
  bank: FeedConnection[];
  /** QuickBooks Online, NetSuite, Xero; shown under Licensed Seller Monitoring. */
  accounting: FeedConnection[];
  /** Live connections that are neither inventory, sales, bank nor accounting. */
  other: FeedConnection[];
  inventoryCsvFiles: CsvFile[];
  salesCsvFiles: CsvFile[];
  /** Banking and accounting CSV uploads. */
  otherCsvFiles: CsvFile[];
}

const ACCOUNTING_SERVICES = ['quickbooks', 'netsuite', 'xero'];

export function isAccountingConnection(value: ConnectionRecord): boolean {
  const slug = `${value.type ?? ''} ${value.service ?? ''} ${value.name ?? ''}`.toLowerCase();
  return ACCOUNTING_SERVICES.some((s) => slug.includes(s));
}

/** Which lists a live (Fivetran) connection belongs to. */
export function feedKindsOf(value: ConnectionRecord): { inventory: boolean; sales: boolean } {
  const platform = detectEcommercePlatform(value);
  if (platform === 'veeqo') return { inventory: true, sales: false };
  if (platform === 'shopify') return { inventory: true, sales: true };
  if (platform === 'amazon') {
    // Vendor Central carries sales only; Seller Central (or unknown) both.
    const account = readStoredAmazonAccountType(value);
    return { inventory: account !== 'vendor', sales: true };
  }
  return { inventory: false, sales: false };
}

// Inventory snapshots carry an _inventory_ marker; other uploads are sales
// (e-commerce) unless the connection says banking or accounting.
const nonInventoryKind = (value: ConnectionRecord): CsvFile['kind'] =>
  value.import_type === 'banking' || value.import_type === 'accounting' ? 'other' : 'sales';

function csvFilesOf(key: string, value: ConnectionRecord): CsvFile[] {
  if (Array.isArray(value.csvImports) && value.csvImports.length > 0) {
    return value.csvImports.map((imp) => {
      const inventory = isInventorySnapshotConnectionId(imp.id);
      return {
        connectionKey: key,
        importId: imp.id,
        fileName: imp.fileName || imp.id || 'Unnamed import',
        uploadedAt: imp.date,
        bindingDate: inventory ? imp.snapshot_date || parseSnapshotDateFromInventoryImportId(imp.id) : undefined,
        kind: inventory ? 'inventory' : nonInventoryKind(value),
      };
    });
  }
  const id = value.id || key;
  const inventory = isInventorySnapshotConnectionId(id);
  const metadataDate = typeof value.metadata?.inventory_date === 'string' ? value.metadata.inventory_date : undefined;
  const fileName = (value as { file_name?: string }).file_name || value.name || id;
  return [{
    connectionKey: key,
    fileName,
    uploadedAt: value.created_at,
    bindingDate: inventory ? metadataDate || parseSnapshotDateFromInventoryImportId(id) : undefined,
    kind: inventory ? 'inventory' : nonInventoryKind(value),
  }];
}

export function groupMerchantFeeds(connections: ConnectionsMap | null | undefined): MerchantFeeds {
  const feeds: MerchantFeeds = {
    inventory: [], sales: [], bank: [], accounting: [], other: [], inventoryCsvFiles: [], salesCsvFiles: [], otherCsvFiles: [],
  };
  for (const [key, value] of connectionEntries(connections)) {
    // The old card skipped service 'file' entries only.
    if (!value || value.service === 'file') continue;
    if (isCsvLikeRecord(value)) {
      for (const file of csvFilesOf(key, value)) {
        const bucket = { inventory: feeds.inventoryCsvFiles, sales: feeds.salesCsvFiles, other: feeds.otherCsvFiles }[file.kind];
        bucket.push(file);
      }
      continue;
    }
    const entry: FeedConnection = { key, value, name: value.name || key };
    if (isPlaidConnectionRecord(value)) {
      feeds.bank.push(entry);
      continue;
    }
    if (isAccountingConnection(value)) {
      feeds.accounting.push(entry);
      continue;
    }
    const kinds = feedKindsOf(value);
    if (kinds.inventory) feeds.inventory.push(entry);
    if (kinds.sales) feeds.sales.push(entry);
    if (!kinds.inventory && !kinds.sales) feeds.other.push(entry);
  }
  return feeds;
}

/** "Oct 14, 2026" for an ISO date or date-time; empty when missing. */
export function formatFeedDate(iso: string | undefined): string {
  if (!iso) return '';
  try {
    return format(parseISO(iso), 'MMM d, yyyy');
  } catch {
    return iso;
  }
}

/** Latest of a set of ISO dates (date or date-time), or undefined. */
export function latestDate(dates: Array<string | undefined>): string | undefined {
  return dates.filter((d): d is string => !!d).sort().at(-1);
}
